/// <reference path="./qrcode.d.ts" />
import {
  AppleSvgXml,
  BackspaceSvgXml,
  ChevronSvgXml,
  CloudSmallSvgXml,
  CloudSvgXml,
  FlagRuSvgXml,
  GoogleSvgXml,
  GoPaySvgXml,
  HelpSvgXml,
  LoaderBgSvgXml,
  LoaderFilledSvgXml,
  McSvgXml,
  PhoneSvgXml,
  QrSvgXml,
  ScanSvgXml,
  StatusSvgXml,
  TopupSvgXml,
  WithdrawSvgXml,
  XSvgXml,
} from '@mobile/assets/kartoy/svg'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { AppIconSbp } from '@shared-ui/components/ui/app-icon/icons/components/sbp-icon'
import { create } from 'qrcode/lib/core/qrcode.js'
import { memo, useMemo } from 'react'
import { G, Path, Rect, Svg, SvgXml } from 'react-native-svg'

const ICONS = {
  apple: AppleSvgXml,
  backspace: BackspaceSvgXml,
  chevron: ChevronSvgXml,
  cloud: CloudSvgXml,
  cloudSmall: CloudSmallSvgXml,
  flagRu: FlagRuSvgXml,
  google: GoogleSvgXml,
  goPay: GoPaySvgXml,
  help: HelpSvgXml,
  phone: PhoneSvgXml,
  scan: ScanSvgXml,
  status: StatusSvgXml,
  topup: TopupSvgXml,
  withdraw: WithdrawSvgXml,
  x: XSvgXml,
} as const

export type KartoyIconName = keyof typeof ICONS

type KartoyIconProps = {
  icon: KartoyIconName
  size?: number
  color?: string
  isInverted?: boolean
}

// Иконки макета kartoy: цвета зашиты в svg из Figma.
// chevron умеет перекраску stroke — для тёмной пиллюли «Назад».
export const KartoyIcon = memo(function KartoyIconComponent({
  color,
  icon,
  size = 24,
  isInverted = false,
}: KartoyIconProps) {
  const kitIcon = { topup: 'topup', withdraw: 'withdraw', help: 'help', x: 'close', chevron: 'chevron-right' } as const

  if (icon in kitIcon && !(isInverted && (icon === 'topup' || icon === 'withdraw'))) {
    return <AppIcon icon={kitIcon[icon as keyof typeof kitIcon]} size={size} color={color} />
  }

  let xml: string = ICONS[icon]

  // Инвертируем градиент денежных кнопок, сохраняя исходную форму иконки.
  if (isInverted && (icon === 'topup' || icon === 'withdraw')) {
    const inverseColors: Record<string, string> = {
      '#F1F1F2': '#232325',
      '#D6D6D7': '#464749',
      '#232325': '#F1F1F2',
      '#464749': '#D6D6D7',
    }
    xml = xml.replace(/#F1F1F2|#D6D6D7|#232325|#464749/g, (value) => inverseColors[value])
  }

  if (icon === 'chevron') {
    xml = color ? ChevronSvgXml.replace(/#919195/g, color) : ChevronSvgXml
  }

  if (icon === 'backspace' && color) {
    xml = BackspaceSvgXml.replace(/#171818/g, color)
  }

  return <SvgXml xml={xml} width={size} height={size} />
})

type ChevronTintedProps = {
  size?: number
  color: string
}

export const KartoyChevronTinted = memo(function KartoyChevronTintedComponent({
  size = 20,
  color,
}: ChevronTintedProps) {
  const xml = ChevronSvgXml.replace(/#919195/g, color)

  return <SvgXml xml={xml} width={size} height={size} />
})

type KartoyQrCodeProps = { size?: number; value?: string }

export const KartoyQrCode = memo(function KartoyQrCodeComponent({ size = 180, value }: KartoyQrCodeProps) {
  const qr = useMemo(() => {
    if (!value) return null
    const { modules } = create(value, { errorCorrectionLevel: 'H' })
    let path = ''

    for (let row = 0; row < modules.size; row += 1) {
      for (let column = 0; column < modules.size; column += 1) {
        if (modules.get(row, column)) path += `M${column + 4},${row + 4}h1v1h-1z`
      }
    }
    return { path, size: modules.size + 8 }
  }, [value])

  if (!qr) return <SvgXml xml={QrSvgXml} width={size} height={size} />
  const logoSize = qr.size * 0.18
  const logoOffset = (qr.size - logoSize) / 2
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${qr.size} ${qr.size}`}>
      <Rect width={qr.size} height={qr.size} fill="white" />
      <Path d={qr.path} fill="black" />
      <Rect x={logoOffset - 1} y={logoOffset - 1} width={logoSize + 2} height={logoSize + 2} fill="white" />
      <G transform={`translate(${logoOffset}, ${logoOffset})`}>
        <AppIconSbp size={logoSize} />
      </G>
    </Svg>
  )
})

export const KartoyLoader = memo(function KartoyLoaderComponent({ size = 24 }: { size?: number }) {
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <SvgXml xml={LoaderBgSvgXml} width={size} height={size} />
      <SvgXml xml={LoaderFilledSvgXml} width={size} height={size} />
    </Svg>
  )
})

export const KartoyMcLogo = memo(function KartoyMcLogoComponent({ width = 96 }: { width?: number }) {
  return <SvgXml xml={McSvgXml} width={width} height={width * 0.1986} />
})
